import {
  Client,
  PricingMode,
  ValidateListingPromoCodeCommand,
  ValidateOrderingPromoCodeCommand,
} from "@repo/rq-api-public";
import { useAtom, useAtomValue } from "jotai";
import { type MouseEvent, useEffect, useRef, useState } from "react";
import {
  listingGuidAtom,
  promoCodeAtom,
  selectedDateAtom,
  selectedPackageAtom,
} from "./widget/store";
import { useQuery } from "@tanstack/react-query";
import { useListingQuery } from "../api/hooks";

export function useBookingStartDateTime() {
  const selectedDate = useAtomValue(selectedDateAtom);
  const selectedPackage = useAtomValue(selectedPackageAtom);

  const startDateTime = new Date(selectedDate.valueOf());
  if (!selectedPackage?.startTime) return startDateTime;

  startDateTime.setHours(selectedPackage.startTime.hour);
  startDateTime.setMinutes(selectedPackage.startTime.minute);
  return startDateTime;
}

export function usePromoCodeDiscountQuery() {
  const [promoCode, setPromoCode] = useAtom(promoCodeAtom);
  const selectedDate = useAtomValue(selectedDateAtom);
  const selectedPackage = useAtomValue(selectedPackageAtom);
  const listingGuid = useAtomValue(listingGuidAtom);
  const { data: listing } = useListingQuery();

  const isCalendarMode = listing?.pricingMode === PricingMode.Calendar;
  const submittedCode = promoCode?.submittedCode;

  const query = useQuery({
    queryFn: () => {
      if (isCalendarMode) {
        return Client.listingsGuidPromoCodeValidationPOST(
          listingGuid!,
          new ValidateListingPromoCodeCommand({
            promoCode: submittedCode!,
            startDate: selectedDate,
          }),
        );
      }
      return Client.pricingsGuidPromoCodeValidationPOST(
        selectedPackage!.pricingGuid!,
        new ValidateOrderingPromoCodeCommand({
          promoCode: submittedCode!,
          startDate: selectedDate,
          startTime: selectedPackage!.startTime!,
        }),
      );
    },
    queryKey: isCalendarMode
      ? [listingGuid, submittedCode, selectedDate, "calendar-promo"]
      : [selectedPackage, submittedCode, selectedDate],
    enabled: isCalendarMode
      ? !!listingGuid && !!submittedCode
      : !!selectedPackage?.pricingGuid &&
        !!submittedCode &&
        !!selectedPackage.startTime,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 0,
  });

  useEffect(() => {
    if (!promoCode) return;
    setPromoCode({
      ...promoCode,
      isLoading: query.isLoading,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isLoading]);

  useEffect(() => {
    if (!query.isError) return;
    // set promo code error and reset submitted code
    setPromoCode({
      submittedCode: null,
      error: "Invalid promo code",
    });
  }, [query.isError, setPromoCode]);

  return query;
}

export const useDisableBodyScroll = (open: boolean) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [open]);
};

export function useHandleOutsideClick<RefType extends HTMLElement>(
  callback: (e: MouseEvent<RefType>) => void,
) {
  const ref = useRef<RefType>(null);

  useEffect(() => {
    /**
     * Run callback if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent<RefType>) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback(event);
      }
    }
    // Bind the event listener
    // oxlint-disable-next-line typescript/no-explicit-any criminal
    document.addEventListener("mousedown", handleClickOutside as any);
    return () => {
      // Unbind the event listener on clean up
      // oxlint-disable-next-line typescript/no-explicit-any criminal
      document.removeEventListener("mousedown", handleClickOutside as any);
    };
  }, [ref, callback]);

  return ref;
}

export function useDebounce<T>(value: T, delay = 500): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
